<i18n>
[
    "sublist__editListItem--deleteFromListCta",
    "closet__editClosetItem--deleteFromClosetCta",
    "closet__editClosetItem--addToListCta"
]
</i18n>

<template>
    <div class="c-closet-badge">
        <BaseMenu
            v-if="isActive"
            class="c-closet-badge__menu"
            :menu="closetActionsMenu"
            :menuProps="{
                choiceId,
                closetListId,
                product,
                productAnalyticsData,
                removeFromListClicked,
                removeFromClosetClicked,
                catalogResponseId,
            }"
            :popoverXOffset="popoverOffsets.x"
            :popoverYOffset="popoverOffsets.y"
            :isTabbable="isTabbable"
            xPosition="right"
        >
            <template #launcher>
                <div
                    :class="{
                        'c-closet-badge__launcher': true,
                        'o-flex-center': true,
                        'c-closet-badge__launcher--hide-padding': hidePadding,
                    }"
                >
                    <div
                        :data-product-tour-lightbox="closetProductTourDataAttribute"
                        class="c-closet-badge__icon-container o-flex-center"
                    >
                        <AppIcon
                            v-if="!isLoading"
                            :icon="'global--hanger'"
                            :size="'16px'"
                            :color="'white'"
                            :transitionDuration="0"
                            class="c-closet-badge__icon"
                        />
                        <LoadingSpinner v-else />
                    </div>
                </div>
            </template>
        </BaseMenu>
        <CoreActionBadge
            v-else
            :icon="'global--hanger'"
            :isSmall="isSmall"
            :isLoading="isLoading"
            :productTourDataAttribute="catalogProductTourDataAttribute"
            :hidePadding="hidePadding"
            :isTabbable="isTabbable"
            @click="toggleBadgeClicked"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';
import { useActions, useMutations } from '~coreModules/core/js/composables/vuexHelpers';

import {
    LAUNCH_MODAL,
    MODALS_MODULE_NAME,
} from '~coreModules/modals/js/modals-store';
import { SET_PRODUCT_CLOSET_STATUS } from '~routes/product/js/product-store';
import {
    TOASTS_MODULE_NAME,
    LAUNCH_TOAST,
} from '~coreModules/toasts/js/toasts-store';
import { TOASTS } from '~modules/toasts/js/toasts-constants';

import {
    CATALOG_TOUR_LIGHTBOXES,
    CLOSET_TOUR_LIGHTBOXES,
} from '~modules/product-tour/js/product-tour-constants';

import {
    TOGGLE_PRODUCT_CLOSET_STATUS,
    CLOSET_ACTIONS_MODULE_NAME,
    REMOVE_PRODUCT_FROM_CLOSET_LIST,
    REMOVE_CHOICE_ID_FROM_CLOSET_LIST,
} from '~routes/closet/js/closet-actions-store';
import { MODALS } from '~modules/modals/js/modal-constants';

import BaseMenu from '~coreModules/menu/components/BaseMenu.vue';
import CoreActionBadge from '~coreModules/core/components/ui/badges/CoreActionBadge.vue';
import LoadingSpinner from '~coreModules/core/components/ui/LoadingSpinner.vue';

import { MENUS } from '~modules/menu/js/menu-constants';

const props = defineProps({
    loggedIn: {
        type: Boolean,
        required: true,
    },
    choiceId: {
        type: String,
        required: true,
    },
    productAnalyticsData: {
        type: Object,
        required: true,
    },
    initialIsActive: {
        type: Boolean,
        required: true,
    },
    popoverOffsets: {
        type: Object,
        required: true,
    },
    isSmall: {
        type: Boolean,
        default: false,
    },
    showInProductTour: {
        type: Boolean,
        default: false,
    },
    closetListId: {
        type: String,
        default: '',
    },
    product: {
        type: Object,
        default: null,
    },
    hidePadding: {
        type: Boolean,
        default: false,
    },
    toastOptions: {
        type: Object,
        default: null,
    },
    catalogResponseId: {
        type: String,
        default: null,
    },
    isTabbable: {
        type: Boolean,
        default: true,
    },
});

const { t } = useI18n();
const route = useRoute();
const productModuleName = computed(() => getProductModuleNameFromRoute(route));

const emit = defineEmits(['click']);

const isActive = ref(props.initialIsActive);
const isLoading = ref(false);
const { closetActionsMenu } = MENUS;

const catalogProductTourDataAttribute = computed(() => (
    props.showInProductTour ? CATALOG_TOUR_LIGHTBOXES.closet.selector : undefined
));

const closetProductTourDataAttribute = computed(() => (
    props.showInProductTour ? CLOSET_TOUR_LIGHTBOXES.closet.selector : undefined
));

const { toggleProductClosetStatus, removeProductFromClosetList } = useActions(CLOSET_ACTIONS_MODULE_NAME, {
    toggleProductClosetStatus: TOGGLE_PRODUCT_CLOSET_STATUS,
    removeProductFromClosetList: REMOVE_PRODUCT_FROM_CLOSET_LIST,
});

const { launchModal } = useActions(MODALS_MODULE_NAME, {
    launchModal: LAUNCH_MODAL,
});

const { removeChoiceIdFromClosetList } = useMutations(CLOSET_ACTIONS_MODULE_NAME, {
    removeChoiceIdFromClosetList: REMOVE_CHOICE_ID_FROM_CLOSET_LIST,
});

const { launchToast } = useMutations(TOASTS_MODULE_NAME, {
    launchToast: LAUNCH_TOAST,
});

const { setProductClosetStatus } = useMutations(productModuleName.value, {
    setProductClosetStatus: SET_PRODUCT_CLOSET_STATUS,
});

function launchSavedToClosetToast() {
    launchToast({
        ...TOASTS.savedToCloset,
        ...props.toastOptions,
        modalOptions: {
            ...MODALS.closetAddToListModal,
            props: {
                choiceId: props.choiceId,
                product: props.product,
                productAnalyticsData: props.productAnalyticsData,
                catalogResponseId: props.catalogResponseId,
            },
        },
        ctaText: t('closet__savedToCloset--addToListCta'),
    });
}

async function removeFromListClicked() {
    await removeProductFromClosetList({
        listId: props.closetListId,
        choiceId: props.choiceId,
        productAnalyticsData: props.productAnalyticsData,
    });

    emit('click');
}

async function removeFromClosetClicked() {
    if (props.closetListId) {
        // calling mutation instead of action as removing from default closet will also remove from list
        removeChoiceIdFromClosetList({
            listId: props.closetListId,
            choiceId: props.choiceId,
        });
    }

    // eslint-disable-next-line no-use-before-define
    await toggleBadgeClicked();
}

async function toggleBadgeClicked() {
    if (!props.loggedIn) {
        launchModal(MODALS.closetJoinModal);
        return;
    }

    if (!isLoading.value) {
        isLoading.value = true;

        try {
            await toggleProductClosetStatus({
                choiceId: props.choiceId,
                isInCloset: isActive.value,
                productAnalyticsData: props.productAnalyticsData,
                catalogResponseId: props.catalogResponseId,
            });
            if (!isActive.value) {
                launchSavedToClosetToast();
            }
            isActive.value = !isActive.value;

            setProductClosetStatus({
                choiceId: props.choiceId,
                newClosetStatus: isActive.value,
            });

            emit('click', isActive.value);
        } finally {
            isLoading.value = false;
        }
    }
}

watch(() => props.choiceId, () => {
    isActive.value = props.product?.inCloset;
});
</script>

<style lang="scss" scoped>
    .c-closet-badge {
        $this: &;
        z-index: map-get($zindex, catalog-tile-badge);

        .c-tooltip {
            &__content {
                padding-top: 0;
            }

            &__body {
                padding: 0;
            }

            &__popover {
                width: auto;
                white-space: nowrap;
            }
        }

        &__button {
            @include caption;
            color: $nu-black;
            text-align: right;
            padding: $nu-spacer-2;
            outline-offset: -4px;
            cursor: pointer;
            &:not(:last-of-type) {
                border-bottom: 1px solid $nu-gray--light;
            }

            @include breakpoint(medium) {
                @include body-copy;
            }
        }

        &__launcher {
            box-sizing: content-box;
            cursor: pointer;
            height: $nu-spacer-4;
            width: $nu-spacer-4;
            padding: $nu-spacer-1;

            &--hide-padding {
                padding: 0;
            }

            @include breakpoint(medium) {
                padding: $nu-spacer-2;

                &--hide-padding {
                    padding: 0;
                }
            }
        }

        &__icon-container {
            height: 100%;
            width: 100%;
            border-radius: 50%;
            border: 1px solid $nu-white;
            background-color: $nu-primary;
            border-color: $nu-primary;
            color: $nu-gray--light;

            .c-tooltip--is-visible &,
            #{$this}__launcher:hover & {
                background-color: $nu-white;
                border-color: $nu-primary;
                color: $nu-primary;

                #{$this}__icon {
                    fill: $nu-primary;
                }
            }
        }
    }
</style>
